import * as UI from "@iyk/ui"
import { Array, Effect, HashMap, Option } from "effect"
import { Assets } from "../../../packages/assets.ts"
import { Handler } from "../../../server/handler.ts"
import { Res } from "../../../server/response.ts"
import type { Profile } from "../profile.ts"
import { AlertCard } from "../ui/alert-card.tsx"
import { ExclusiveCardList } from "../ui/exclusive-card-list.tsx"
import { FestivalMap } from "../ui/festival-map.tsx"
import { Link, Routes } from "../ui/link.tsx"
import { ControlledLoginSheet } from "../ui/login-sheet.tsx"
import { Stations } from "../ui/stations.tsx"
import { UserProfileCard } from "../ui/user-profile-card.tsx"
import type { Route } from "./+types/home.ts"

type LoaderData = {
  exclusives: Array<Profile.Exclusive>
  checkInData: {
    questLocations: Array<{ id: string; name: string; thumbnail: string; userCheckedIn: boolean }>
    totalCheckIns: number
  }
}

export const loader = Handler.make<LoaderData>(
  "profile-home",
  ({ api, sessions, locations, locationCheckIns }) => {
    const checkInData = Effect.gen(function* () {
      const session = yield* sessions.authenticated.pipe(Effect.option)

      const foundLocations = yield* locations.list
      const questLocations = Array.filter(foundLocations, (l) => !l.value.cmsData.isVip)
      if (Option.isNone(session)) {
        return {
          totalCheckIns: 0,
          questLocations: questLocations.map((l) => ({
            id: l.id.value,
            name: l.value.name,
            thumbnail: l.value.thumbnail.href,
            userCheckedIn: false,
          })),
        }
      }

      const userCheckIns = yield* locationCheckIns.findByUserId(session.value.user.id)
      const userCheckInsByLocationId = HashMap.fromIterable(
        userCheckIns.map((checkIn) => [checkIn.value.locationId, checkIn]),
      )

      return {
        totalCheckIns: userCheckIns.length,
        questLocations: questLocations.map((l) => {
          const userCheckIn = HashMap.get(userCheckInsByLocationId, l.id)
          return {
            id: l.id.value,
            name: l.value.name,
            thumbnail: l.value.thumbnail.href,
            userCheckedIn: Option.isSome(userCheckIn),
          }
        }),
      }
    })

    return Effect.all(
      {
        checkInData,
        exclusives: sessions.authenticated.pipe(
          Effect.flatMap((session) => api.findUnlockedExclusives(session.user.id)),
          Effect.orElseSucceed(() => []),
        ),
      },
      { concurrency: "unbounded" },
    ).pipe(Effect.map(Res.json))
  },
)

export default function ProfileRoute(props: Route.ComponentProps) {
  const layoutData = props.matches[1].data
  const { checkInData, exclusives } = props.loaderData

  return layoutData.user ? (
    <LoggedInProfile
      user={layoutData.user}
      exclusives={exclusives}
      stations={checkInData.questLocations}
    />
  ) : (
    <LoggedOutProfile stations={checkInData.questLocations} />
  )
}

const LoggedInProfile = ({ user, alert, exclusives, stations }: LoggedInProfile.Props) => (
  <div className="flex flex-col gap-8 px-4">
    <UserProfileCard
      userId={user.id}
      username={user.username}
      since={user.memberSince}
      totalCheckIns={user.visitedLocationIds.length}
      variant="profile"
    />
    {alert && (
      <AlertCard
        eyebrow={alert.eyebrow}
        title={alert.title}
        subtitle={alert.subtitle}
        announcementTitle={alert.announcementTitle}
        announcementDate={alert.announcementDate}
        description={alert.description}
        kind={alert.kind}
      />
    )}

    {exclusives.length > 0 && (
      <section className="flex flex-col gap-4">
        <h2 className="text-base">Your Exclusives</h2>
        <div className="grid gap-2">
          <ExclusiveCardList owned={true} exclusives={exclusives} />
        </div>
      </section>
    )}

    <Stations stations={stations} profileId={user.id} />
    <FestivalMap />
    <ShopMerchSection />
  </div>
)

namespace LoggedInProfile {
  export interface Props {
    user: Profile.User
    exclusives: Array<Profile.Exclusive>
    stations: Stations.Props["stations"]
    alert?: AlertCard.Props
  }
}

// TODO: add login button and auth sheet
const LoggedOutProfile = ({ stations }: { stations: Stations.Props["stations"] }) => (
  <div className="flex flex-col gap-8 px-4">
    <section
      style={{
        backgroundImage: `linear-gradient(rgb(0_0_0/30%),rgb(0_0_0/30%)),url('${Assets.images.emptyCollectionBackground}')`,
      }}
      className="aspect-[3/4] flex flex-col items-center justify-center p-8 text-sm text-center text-balance relative bg-cover rounded backdrop-blur-sm [box-shadow:0_25px_50px_-12px_rgb(0_0_0/50%),rgb(100%_100%_100%/10%)_0_0_0_.5px_inset]"
    >
      <p className="mt-3 text-balance">
        Create a Slander profile for a chance to win VIP upgrade + merch bundle
      </p>
      <ControlledLoginSheet returnTo={Routes.home} user={undefined}>
        {(open) => (
          <UI.Button type="button" kind="outline" size="sm" className="mt-4" onClick={open}>
            Get started
          </UI.Button>
        )}
      </ControlledLoginSheet>
    </section>

    <Stations stations={stations} />
    <FestivalMap />
    <ShopMerchSection />
  </div>
)

const CheckInCard = ({ title, description }: CheckInCard.Props) => (
  <section className="flex flex-col gap-4 p-4 bshadow bshadow-gray-5">
    <div className="grid gap-1">
      <h3 className="text-sm text-gray-12">{title}</h3>
      <p className="text-sm text-gray-11">{description}</p>
    </div>
    <Link
      to={Routes.checkIn}
      className={UI.classForButton({ size: "sm", kind: "solid", className: "w-max" })}
    >
      Check in
    </Link>
  </section>
)

namespace CheckInCard {
  export interface Props {
    title: string
    description: string
  }
}

const ShopMerchSection = () => (
  <section className="flex flex-col gap-4">
    <h2 className="text-base">Shop merch</h2>
    <a
      target="_blank"
      rel="noreferrer"
      href="https://slanderofficial.com/?utm_source=iyk&utm_medium=app&utm_campaign=starbase_2025&utm_content=shop-module"
      style={{ backgroundImage: `url(${Assets.images.shopMerch.dark})` }}
      className="aspect-video bg-cover bg-center flex flex-col justify-end items-end p-2"
    >
      <UI.Button type="button" kind="shaded" size="sm" className="flex gap-1 items-center">
        <p className="text-sm">Explore styles</p>
        <UI.Icon.ArrowUpRight />
      </UI.Button>
    </a>
  </section>
)
